'use client';

import { useCallback, useEffect, useState, useTransition, MouseEvent } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useNotification from '@/hooks/useNotification';
import useTranslations from '@/hooks/useTranslations';

import useAuth from '@/lib/firebase/auth/store';
import toggleNewsletterSubscription from '@/actions/user/toggleNewsletterSubscription';

export default function Newsletter() {
  const [isPending, startTransition] = useTransition();
  const { userData } = useAuth();
  const [subscribed, setSubscribed] = useState(() => !!userData?.newsletter);
  const notifications = useNotification();
  const { t } = useTranslations();
  const { userId } = useAuth();

  const handleChange = useCallback(
    async (_: MouseEvent<HTMLElement>, subscribed: boolean) =>
      startTransition(async () => {
        setSubscribed(subscribed);

        await toggleNewsletterSubscription({
          userId: userId!,
          subscribed,
        });

        notifications.success(subscribed ? t('footer.suscribed') : t('footer.unsubscribed'));
      }),
    [t, userId, notifications],
  );

  useEffect(() => {
    if (userData?.newsletter !== undefined) {
      setTimeout(() => {
        setSubscribed(!!userData.newsletter);
      }, 0);
    }
  }, [userData?.newsletter]);

  return (
    <Stack direction='column' spacing={0.75} sx={{ maxWidth: { xs: '100%', md: 200 } }}>
      <Typography
        variant='body1'
        sx={{
          color: 'text.primary',
          fontWeight: 'bold',
        }}
      >
        {t('footer.newsletter')}
      </Typography>

      <Stack direction='column' spacing={0.5}>
        <Typography variant='body2' color='text.secondary'>
          {t('footer.getUpdates')}
        </Typography>

        <ToggleButtonGroup
          value={subscribed}
          onChange={handleChange}
          color='secondary'
          size='small'
          exclusive
          disabled={isPending}
        >
          <ToggleButton value={false}>{t('footer.off')}</ToggleButton>
          <ToggleButton value={true}>{t('footer.on')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
